import { useState } from 'react';
import { X, Check, Copy } from 'lucide-react';
import { Dialog, DialogContent, DialogVisuallyHidden } from '@/components/ui/dialog';
import LinkedinIcon from '@/assets/social/linkedin.svg?react';
import WhatsAppIcon from '@/assets/social/whatsapp.svg?react';
import FacebookIcon from '@/assets/social/facebook.svg?react';
import EmailIcon from '@/assets/social/email.svg?react';
import RedditIcon from '@/assets/social/reddit.svg?react';
import XIcon from '@/assets/social/x.svg?react';

interface ShareModalProps {
	open: boolean;
	url: string;
	message: string;
	onClose: () => void;
}

const withMessageAndUrl = (message: string, url: string) =>
	message ? `${message}\n\n${url}` : url;

const SOCIAL_PLATFORMS = [
	{
		key: 'whatsapp',
		label: 'WhatsApp',
		icon: <WhatsAppIcon />,
		getUrl: (url: string, message: string) =>
			`https://wa.me/?text=${encodeURIComponent(withMessageAndUrl(message, url))}`,
	},
	{
		key: 'linkedin',
		label: 'LinkedIn',
		icon: <LinkedinIcon />,
		getUrl: (url: string) =>
			`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
	},
	{
		key: 'facebook',
		label: 'Facebook',
		icon: <FacebookIcon />,
		getUrl: (url: string, _message: string) =>
			`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
	},
	{
		key: 'x',
		label: 'X',
		icon: <XIcon />,
		getUrl: (url: string, message: string) => {
			const params = new URLSearchParams({ url });
			if (message) params.set('text', message);
			return `https://twitter.com/intent/tweet?${params.toString()}`;
		},
	},
	{
		key: 'email',
		label: 'Email',
		icon: <EmailIcon />,
		getUrl: (url: string, message: string) =>
			`mailto:?subject=${encodeURIComponent(message || 'Join me!')}&body=${encodeURIComponent(withMessageAndUrl(message, url))}`,
	},
	{
		key: 'reddit',
		label: 'Reddit',
		icon: <RedditIcon />,
		getUrl: (url: string, message: string) =>
			`https://www.reddit.com/submit?url=${encodeURIComponent(url)}&title=${encodeURIComponent(message)}`,
	},
];

export function ShareModal({ open, url, message, onClose }: ShareModalProps) {
	const [copied, setCopied] = useState(false);

	const handleCopy = async () => {
		try {
			await navigator.clipboard.writeText(url);
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		} catch {
			// ignore
		}
	};

	const handleSocial = (platform: (typeof SOCIAL_PLATFORMS)[number]) => {
		const shareUrl = platform.getUrl(url, message);
		if (platform.key === 'email') {
			window.location.assign(shareUrl);
		} else {
			window.open(shareUrl, '_blank', 'noopener,noreferrer');
		}
	};

	return (
		<Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
			<DialogContent
				hideCancelIcon
				className="w-full max-w-[563px] rounded-2xl border border-[#E5E5E5] bg-white p-0 font-['DM_Sans'] shadow-[0px_1px_2px_rgba(12,12,13,0.1),0px_1px_2px_rgba(12,12,13,0.05)] gap-8"
			>
				<DialogVisuallyHidden title="Share to" />

				{/* Header */}
				<div className="flex items-center justify-between px-4 pt-3.5">
					<div className="w-6" />
					<p className="flex-1 text-center text-[16px] font-bold leading-[22px] tracking-[-0.6px] text-[#0A0A0A]">
						Share to
					</p>
					<button
						onClick={onClose}
						className="rounded p-1 text-[#474747] transition-colors hover:text-[#0A0A0A]"
						aria-label="Close"
					>
						<X className="size-6" />
					</button>
				</div>

				{/* Social icons */}
				<div className="hide-scrollbar flex items-start gap-4 overflow-x-auto px-6 pb-1">
					{SOCIAL_PLATFORMS.map((platform) => (
						<button
							key={platform.key}
							onClick={() => handleSocial(platform)}
							className="flex shrink-0 flex-col items-center gap-2 transition-opacity hover:opacity-80"
							aria-label={`Share on ${platform.label}`}
						>
							<div>{platform.icon}</div>
							<span className="text-[12px] font-normal leading-normal tracking-[0.4px] text-[#0F0F0F]">
								{platform.label}
							</span>
						</button>
					))}
				</div>

				{/* URL field + Copy button */}
				<div className="flex items-center gap-4 px-6 pb-6 max-w-[563px]">
					<div className="flex h-14 flex-1 min-w-0 items-center rounded-lg border border-[#E5E5E5] bg-[#F3F3F5] px-4 overflow-hidden">
						<p className="w-full truncate text-[16px] font-normal leading-[22px] tracking-[-0.512px] text-[#474747]">
							{url}
						</p>
					</div>
					<button
						onClick={handleCopy}
						className="flex h-14 w-[125px] shrink-0 items-center justify-center gap-2 rounded-lg text-[16px] font-bold leading-[22px] tracking-[-0.6px] text-white transition-opacity hover:opacity-90"
						style={{
							background: '#2667FF',
							border: '0.5px solid #5583F6',
							boxShadow: '0px 8px 16px 0px rgba(38,103,255,0.24), 0px 2px 6px 0px rgba(0,0,0,0.15)',
						}}
					>
						{copied ? (
							<>
								Copied
								<Check className="size-5" />
							</>
						) : (
							<>
								Copy
								<Copy className="size-5" />
							</>
						)}
					</button>
				</div>
			</DialogContent>
		</Dialog>
	);
}
